import { Box, Button, TextareaAutosize, Typography } from "@outschool/backpack";
import {
  ChildFragmentFragment,
  SearchFilters
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { useTrackEvent } from "@outschool/ui-analytics";
import React, { useEffect, useState } from "react";

import { useActiveLearner } from "../../providers/ActiveLearnerProvider";
import { LearnerSelection } from "../chat/components/LearnerSelection";
import { LearnerInfo } from "./buildUserMessage";
import { useConciergeRequest } from "./useConciergeRequest";

export function ConciergeTextInput({
  submitTrackingEvent,
  searchFilters
}: {
  submitTrackingEvent: string;
  searchFilters?: SearchFilters;
}) {
  const { t } = useTranslation(
    "client\\components\\concierge\\ConciergeTextInput"
  );
  const [inputValue, setInputValue] = useState("");
  const { activeLearner } = useActiveLearner();
  const [selectedLearner, setSelectedLearner] = useState<
    ChildFragmentFragment | LearnerInfo
  >(null);

  useEffect(() => {
    setSelectedLearner(activeLearner);
  }, [activeLearner]);

  const {
    isSubmitting,
    successMessage,
    errorMessage,
    submitRequest,
    clearMessages
  } = useConciergeRequest();

  const trackEvent = useTrackEvent();
  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(event.target.value);
    if (successMessage || errorMessage) {
      clearMessages();
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    trackEvent(submitTrackingEvent);

    const finalInputValue = searchFilters
      ? inputValue +
        `--- Parent search filters --- \n${JSON.stringify(searchFilters)}`
      : inputValue;

    const success = await submitRequest(finalInputValue, selectedLearner);
    if (success) {
      setInputValue("");
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box flex flexDirection="column" gap="16px">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "0.8rem",
            padding: "1.6rem",
            border: "1px solid",
            borderColor: errorMessage ? "error.main" : "grey.300",
            backgroundColor: "common.white",
            borderRadius: "4px",
            "&:focus-within": {
              borderColor: errorMessage ? "error.main" : "primary.main"
            }
          }}
        >
          <LearnerSelection
            label={t("Learner")}
            selectedLearner={selectedLearner}
            onLearnerSelect={setSelectedLearner}
            color="neutral"
          />
          <TextareaAutosize
            id="concierge-text-input"
            placeholder={t(
              "E.g., My 10-year-old loves animals and needs evening science classes."
            )}
            value={inputValue}
            onChange={handleInputChange}
            sx={{
              border: "none",
              padding: 0,
              color: "text.primary",
              resize: "none",
              "&:focus": {
                outline: "none"
              }
            }}
            multiline
            fullWidth
            minRows={2}
            disabled={isSubmitting}
          />
        </Box>

        {successMessage && (
          <Typography color="success.main" variant="body2">
            {successMessage}
          </Typography>
        )}

        {errorMessage && (
          <Typography color="error.main" variant="body2">
            {errorMessage}
          </Typography>
        )}

        <Button
          type="submit"
          variant="contained"
          size="small"
          color="primary"
          sx={{ alignSelf: "start" }}
          disabled={isSubmitting || !inputValue.trim()}
        >
          {isSubmitting ? t("Sending...") : t("Send to Concierge")}
        </Button>
      </Box>
    </form>
  );
}
